import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import { Navigate, Outlet, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HelpWidget from '../ui/HelpWidget';
import Layout from './Layout';
import ConfirmSignUp from '../modals/ConfirmSignUp';
import SignIn from '../../views/login/SignIn';
import SignUp from '../../views/login/SignUp';
import { ResetPassword } from '../../views/login/ResetPassword';
import PendingApproval from '../../views/login/PendingApproval';
import PricingPage from '../../views/pricing/PricingPage';
import SubscriptionPage from '../../views/subscription/SubscriptionPage';
import ScribeMain from '../../views/scribe/ScribeMain';
import LandingPage from '../../views/landing/LandingPage';
import TermsOfServicePage from '../../views/legal/TermsOfServicePage';
import PrivacyPolicyPage from '../../views/legal/PrivacyPolicyPage';
import RefundPolicyPage from '../../views/legal/RefundPolicyPage';
import AccessBlockedModal from '../subscription/AccessBlockedModal';

interface AuthFormState {
  email: string;
  message: string;
}

const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 2,
      backgroundColor: 'var(--bg)',
      color: 'var(--t1)',
    }}
  >
    <CircularProgress sx={{ color: 'var(--teal)' }} />
    <span>Loading...</span>
  </Box>
);

const AuthenticatedShell: React.FC<{ userName: string; onSignOut: () => Promise<void>; showTools: boolean }> = ({
  userName,
  onSignOut,
  showTools,
}) => (
  <>
    <Layout userName={userName} onSignOut={onSignOut} showTools={showTools} />
    <HelpWidget />
  </>
);

const AppContent: React.FC = () => {
  const {
    user,
    isAuthenticated,
    isApproved,
    canUseTools,
    trialState,
    isLoading,
    refreshStatus,
    signOut,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authFormState, setAuthFormState] = useState<AuthFormState>({ email: '', message: '' });
  const [subscriptionToast, setSubscriptionToast] = useState(false);

  useEffect(() => {
    if (searchParams.get('subscribed') === '1') {
      setSubscriptionToast(true);
      searchParams.delete('subscribed');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cleanUserName = useMemo(() => {
    if (!user?.name) return 'User';
    if (!user.email) return user.name;
    return user.name.replace(user.email, '').trim() || user.name;
  }, [user?.email, user?.name]);

  const handleSignInSuccess = useCallback(async () => {
    setAuthFormState({ email: '', message: '' });
    await refreshStatus();
    // Trial-active and paid users go straight to the app. Only fully blocked
    // users (trial expired or no subscription at all) land on the pricing
    // page. AuthContext will have populated trialState by now.
    navigate('/', { replace: true });
  }, [navigate, refreshStatus]);

  const handleSignInFailure = useCallback((error: any, email?: string) => {
    setAuthFormState({
      email: email || '',
      message: error?.message || 'Sign in failed. Please try again.',
    });
  }, []);

  const handleSignUpSuccess = useCallback(
    (email: string) => {
      setAuthFormState({
        email,
        message: 'Account created successfully. Please check your email for a verification code.',
      });
      navigate('/confirm-signup');
    },
    [navigate],
  );

  const handleSignUpFailure = useCallback((error: any) => {
    setAuthFormState({
      email: '',
      message: error?.message || String(error) || 'Sign up failed. Please try again.',
    });
  }, []);

  const handleConfirmSignUpSuccess = useCallback(() => {
    setAuthFormState({ email: '', message: 'Account confirmed successfully. Please sign in.' });
    navigate('/login');
  }, [navigate]);

  const handleConfirmSignUpFailure = useCallback(
    (error: any) => {
      setAuthFormState({
        email: authFormState.email,
        message: error?.message || 'Confirmation failed. Please try again.',
      });
    },
    [authFormState.email],
  );

  const handleResetPasswordSuccess = useCallback(() => {
    setAuthFormState({ email: '', message: 'Password reset successfully. Please sign in.' });
    navigate('/login');
  }, [navigate]);

  const handleResetPasswordFailure = useCallback((error: any) => {
    setAuthFormState({
      email: '',
      message: error?.message || 'Password reset failed. Please try again.',
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/', { replace: true });
  }, [navigate, signOut]);

  if (isLoading) return <LoadingScreen />;

  // Trial-active counts as "ready for app". Fully-blocked users (no
  // subscription, or trial_expired without an upgrade) get routed to /pricing.
  const isReadyForApp = isAuthenticated && (canUseTools || isApproved);

  return (
    <>
    <Routes>
      <Route
        path="/login"
        element={
          isReadyForApp ? (
            <Navigate to="/" replace />
          ) : (
            <SignIn
              onSignInSuccess={handleSignInSuccess}
              onSignInFailure={handleSignInFailure}
              onNavigateToSignUp={() => navigate('/signup')}
              onNavigateToResetPassword={() => navigate('/reset-password')}
              onNavigateToConfirmSignUp={() => navigate('/confirm-signup')}
              externalError={authFormState.message}
            />
          )
        }
      />
      <Route
        path="/signup"
        element={
          isReadyForApp ? (
            <Navigate to="/" replace />
          ) : (
            <SignUp
              onSignUpSuccess={handleSignUpSuccess}
              onSignUpFailure={handleSignUpFailure}
              onNavigateToSignIn={() => navigate('/login')}
            />
          )
        }
      />
      <Route
        path="/confirm-signup"
        element={
          <ConfirmSignUp
            email={authFormState.email}
            onConfirmSuccess={handleConfirmSignUpSuccess}
            onConfirmFailure={handleConfirmSignUpFailure}
          />
        }
      />
      <Route
        path="/reset-password"
        element={
          <ResetPassword
            onResetSuccess={handleResetPasswordSuccess}
            onResetFailure={handleResetPasswordFailure}
          />
        }
      />
      <Route
        path="/pending-approval"
        element={
          isAuthenticated ? (
            <Navigate to="/pricing" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        element={
          isReadyForApp ? (
            <AuthenticatedShell userName={cleanUserName} onSignOut={handleSignOut} showTools />
          ) : isAuthenticated ? (
            // Authed but trial-expired / no subscription — render the same shell so
            // they can reach /pricing without having to sign in again. No tool items: Scribe would
            // bounce them straight back to /pricing.
            <AuthenticatedShell userName={cleanUserName} onSignOut={handleSignOut} showTools={false} />
          ) : (
            <Outlet />
          )
        }
      >
        <Route
          index
          element={
            isReadyForApp ? (
              <ScribeMain isApproved={canUseTools} />
            ) : isAuthenticated ? (
              <Navigate to="/pricing" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route
          path="/subscription"
          element={isReadyForApp ? <SubscriptionPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/pricing"
          element={isAuthenticated ? <PricingPage /> : <Navigate to="/login" replace />}
        />
      </Route>

      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/refund" element={<RefundPolicyPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

    <Snackbar
      open={subscriptionToast}
      autoHideDuration={6000}
      onClose={() => setSubscriptionToast(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSubscriptionToast(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
        Subscription activated successfully! Welcome aboard.
      </Alert>
    </Snackbar>

    {isAuthenticated && <AccessBlockedModal />}
    </>
  );
};

export default AppContent;
